import ReactMarkdown from 'react-markdown';
import { useState } from 'react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  RefreshCw, BookMarked, ShieldCheck, Wand2, PenTool, Sparkles, Loader2, Plus,
} from 'lucide-react';
import AIAssistanceBadge from '@/components/AIAssistanceBadge';
import ChapterLiveEditor from '@/components/ChapterLiveEditor';
import { numberCitations } from '@/lib/numberCitations';
import { resolveAssetRefs } from '@/lib/resolveAssetRefs';
import { formatVancouver } from '@/lib/formatCitation';
import { Telescope, Trophy } from 'lucide-react';

/**
 * Center pane of the 3-column ThesisWorkspace — chapter editor + toolbar +
 * citation verifier summary + reading/edit toggle + sources footer.
 *
 * Sprint 7: each paragraph in Reading mode renders an "+ Add here" button
 * that opens the SmartContinueDialog anchored to that paragraph.
 */
export default function ChapterEditorPanel({
  openChapter,
  userId,
  projectId,
  assetLabels,
  editorMode,
  continuing,
  aiLanguage,
  onLoadVersions,
  onOpenEvidence,
  onOpenCompliance,
  onContinueWriting,
  onToggleEditorMode,
  onOpenPlagiarism,
  onRunReview,
  onChapterSaved,
  onCancelGeneration,
  onOpenWaveD,
  onOpenReadiness,
  onSmartContinue,        // (precedingText, followingText, anchorLabel) => void
  onRefreshChapter,       // (chapterId) => void — reload one chapter after re-verification
}) {
  const [reverifying, setReverifying] = useState(false);
  if (!openChapter) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Select a section from the list
        </CardContent>
      </Card>
    );
  }

  // Sprint 6 fix — show a banner when this chapter is stuck in
  // premium_status='running' so the user can cancel it cleanly.
  const isRunning = openChapter.premium_status === 'running';
  const updatedAt = openChapter.updated_at ? Date.parse(openChapter.updated_at) : 0;
  const stuckMins = updatedAt ? Math.round((Date.now() - updatedAt) / 60000) : 0;

  const { processedMarkdown, references } = numberCitations(
    resolveAssetRefs(openChapter.markdown || '', assetLabels?.labels, assetLabels),
    openChapter.citations || [],
  );

  return (
    <Card data-testid="chapter-editor-scroll-anchor">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="font-serif">{openChapter.title}</CardTitle>
            <div className="mt-1.5 mb-1">
              <AIAssistanceBadge
                reviewed={!!openChapter.reviewed_by_human}
                hasReview={!!openChapter.last_review_id}
                lang="en"
              />
            </div>
            <CardDescription>
              {openChapter.chunks_used} sources · {openChapter.citations?.length || 0} unique citations · model: {openChapter.model_used}
              {openChapter.style_examples_used > 0 && <> · <span className="text-amber-700 dark:text-amber-400">style: {openChapter.style_examples_used} refs</span></>}
              {openChapter.previous_chapters_used > 0 && <> · {openChapter.previous_chapters_used} prior sections in context</>}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <Button variant="outline" size="sm" onClick={() => onLoadVersions(openChapter.id)}
              data-testid={`chapter-versions-${openChapter.id}`}>
              <RefreshCw className="w-3 h-3 mr-1" /> History
            </Button>
            <Button variant="outline" size="sm" onClick={onOpenEvidence}
              data-testid={`chapter-evidence-${openChapter.id}`}>
              <BookMarked className="w-3 h-3 mr-1" /> Sources
            </Button>
            <Button variant="outline" size="sm"
              className="border-teal-700/40 text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-950/40"
              onClick={() => onOpenCompliance(openChapter)}
              data-testid={`chapter-compliance-${openChapter.id}`}>
              <ShieldCheck className="w-3 h-3 mr-1" /> EQUATOR
            </Button>
            <Button variant="outline" size="sm"
              onClick={onContinueWriting} disabled={continuing}
              className="border-[#1d4ed8]/40 text-[#1d4ed8] hover:bg-[#1d4ed8]/5 dark:text-[#7aa9ff]"
              data-testid={`chapter-continue-${openChapter.id}`}>
              {continuing
                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                : <Wand2 className="w-3 h-3 mr-1" />}
              Continue
            </Button>
            {onSmartContinue && (
              <Button variant="outline" size="sm"
                onClick={() => onSmartContinue(openChapter.markdown || '', '', 'the end of the section')}
                className="border-violet-500/40 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-950/40 dark:text-violet-300"
                data-testid={`chapter-smart-continue-${openChapter.id}`}
                title="Continue writing with an optional hint about WHAT to add next">
                <Sparkles className="w-3 h-3 mr-1" /> Continue here…
              </Button>
            )}
            <Button variant={editorMode ? 'default' : 'outline'} size="sm"
              onClick={onToggleEditorMode}
              className={editorMode ? 'bg-[#1d4ed8] hover:bg-[#1e40af] text-white' : 'border-[#1d4ed8]/40 text-[#1d4ed8] hover:bg-[#1d4ed8]/5 dark:text-[#7aa9ff]'}
              data-testid={`chapter-edit-toggle-${openChapter.id}`}>
              <PenTool className="w-3 h-3 mr-1" />
              {editorMode ? 'Reading' : 'Live edit'}
            </Button>
            <Button variant="outline" size="sm"
              onClick={onOpenPlagiarism}
              className="border-amber-500/40 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
              data-testid={`chapter-originality-${openChapter.id}`}>
              <ShieldCheck className="w-3 h-3 mr-1" /> Originality
            </Button>
            <Button variant="default" size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => onRunReview(openChapter.id)}
              data-testid={`chapter-review-${openChapter.id}`}>
              <Sparkles className="w-3 h-3 mr-1" /> AI Review
            </Button>
            {onOpenWaveD && (
              <Button variant="default" size="sm"
                className="bg-violet-600 hover:bg-violet-700 text-white"
                onClick={onOpenWaveD}
                data-testid={`chapter-wave-d-${openChapter.id}`}
                title="Statistical / Gap / Novelty analyzers">
                <Telescope className="w-3 h-3 mr-1" /> Analyze
              </Button>
            )}
            {onOpenReadiness && (
              <Button variant="default" size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={onOpenReadiness}
                data-testid={`chapter-readiness-${openChapter.id}`}
                title="Pre-publish readiness audit — runs all 7 checkers and aggregates into a single 0-100 score">
                <Trophy className="w-3 h-3 mr-1" />
                Readiness{openChapter.last_readiness?.score != null
                  ? ` ${openChapter.last_readiness.score}` : ''}
              </Button>
            )}
          </div>
        </div>

        {openChapter.verification && (
          <div className="mt-3 flex items-center gap-3 p-3 rounded-md bg-muted/40 border" data-testid="chapter-verification">
            <div className={`text-2xl font-bold ${
              openChapter.verification.summary.trust_score >= 0.9 ? 'text-green-600 dark:text-green-400' :
              openChapter.verification.summary.trust_score >= 0.6 ? 'text-amber-600 dark:text-amber-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              {Math.round(openChapter.verification.summary.trust_score * 100)}%
            </div>
            <div className="text-xs text-muted-foreground leading-tight">
              <div className="font-medium text-foreground">Citation Verifier</div>
              {openChapter.verification.summary.verified}/{openChapter.verification.summary.total} citations verified against sources.
              {openChapter.verification.summary.unverified > 0 && (
                <span className="text-red-600 dark:text-red-400">
                  {' '}({openChapter.verification.summary.unverified} needing review)
                </span>
              )}
            </div>
            <Button variant="outline" size="sm" className="ml-auto shrink-0"
              disabled={reverifying}
              onClick={async () => {
                setReverifying(true);
                try {
                  await thesisApi.reverifyChapter(userId, openChapter.id);
                  toast.success('Citations re-checked against the current library');
                  onRefreshChapter?.(openChapter.id);
                } catch (e) {
                  toast.error(e?.response?.data?.detail?.error
                    || e?.response?.data?.detail || 'Re-verification failed');
                } finally { setReverifying(false); }
              }}
              title="Re-check every citation against the library — run this after adding sources"
              data-testid={`chapter-reverify-${openChapter.id}`}>
              {reverifying
                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                : <RefreshCw className="w-3 h-3 mr-1" />}
              Re-verify
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {isRunning && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-4 py-3 flex items-start gap-3" data-testid="chapter-running-banner">
            <Loader2 className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 animate-spin shrink-0" />
            <div className="flex-1 text-sm text-amber-900 dark:text-amber-200">
              <p className="font-medium">Generation in progress · {stuckMins} min elapsed</p>
              <p className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-0.5">
                {openChapter.premium_progress || 'Queued — waiting for engine.'} The card will refresh automatically when done.
              </p>
            </div>
            {onCancelGeneration && (
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                onClick={() => onCancelGeneration(openChapter.id)}
                data-testid={`chapter-cancel-${openChapter.id}`}
              >
                Cancel
              </Button>
            )}
          </div>
        )}
        {editorMode ? (
          <ChapterLiveEditor
            key={openChapter.id}
            userId={userId}
            projectId={projectId}
            chapter={openChapter}
            language={aiLanguage}
            onChapterSaved={onChapterSaved}
            onClose={onToggleEditorMode}
          />
        ) : (
          <article className="prose prose-sm dark:prose-invert max-w-none thesis-md"
            data-testid="chapter-content"
            data-references-count={references.length}
            data-selection-scope>
            <ReactMarkdown
              components={{
                p: ({ children, node }) => {
                  // Extract plain text from this paragraph to compute insertion anchor
                  const paragraphText = node?.children?.map((c) => c?.value || '').join('') || '';
                  const fullMd = openChapter.markdown || '';
                  // Find the paragraph's position in the source markdown — case-sensitive,
                  // first occurrence. Falls back to end-of-text if not found.
                  const handleAddHere = () => {
                    if (!onSmartContinue) return;
                    let idx = paragraphText.length >= 30 ? fullMd.indexOf(paragraphText.slice(0, 80)) : -1;
                    if (idx === -1) {
                      // fallback: append at the very end
                      onSmartContinue(fullMd, '', 'the end of the section');
                      return;
                    }
                    const endIdx = idx + paragraphText.length;
                    const before = fullMd.slice(0, endIdx);
                    const after = fullMd.slice(endIdx);
                    const preview = paragraphText.slice(0, 60).trim() + (paragraphText.length > 60 ? '…' : '');
                    onSmartContinue(before, after, `"${preview}"`);
                  };
                  return (
                    <p className="my-3 leading-relaxed group relative">
                      {children}
                      {onSmartContinue && paragraphText.length > 40 && (
                        <button
                          type="button"
                          onClick={handleAddHere}
                          className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded
                            text-violet-700 bg-violet-100 hover:bg-violet-200 dark:text-violet-300 dark:bg-violet-950/40 dark:hover:bg-violet-900/40"
                          title="Add AI-generated continuation after this paragraph"
                          data-testid="paragraph-add-here">
                          <Plus className="h-2.5 w-2.5" /> add here
                        </button>
                      )}
                    </p>
                  );
                },
              }}
            >
              {processedMarkdown || '*(empty)*'}
            </ReactMarkdown>
          </article>
        )}

        {openChapter.citations?.length > 0 && (
          <div className="mt-6 pt-4 border-t border-border">
            <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Sources in this section</h4>
            <ol className="space-y-2 text-xs list-none pl-0">
              {openChapter.citations.map((cit, i) => {
                const vancouver = formatVancouver(cit, i + 1);
                return (
                  <li key={cit.manubot_ref || cit.doc_id || `cit-${i}`} className="leading-relaxed">
                    <span className="text-foreground">{vancouver}</span>
                    {cit.doi && (
                      <a className="ml-2 text-xs text-blue-600 dark:text-blue-400 underline"
                        href={`https://doi.org/${cit.doi}`} target="_blank" rel="noopener noreferrer">DOI link</a>
                    )}
                    {cit.pmid && (
                      <a className="ml-2 text-xs text-blue-600 dark:text-blue-400 underline"
                        href={`https://pubmed.ncbi.nlm.nih.gov/${cit.pmid}/`} target="_blank" rel="noopener noreferrer">PubMed</a>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
